/**
 * Resource manager — ARCHITECTURE.md sections 5.5.1 through 5.5.6.
 *
 * Enforces hard stops (token budget, node/edge ceiling, wall-clock timeout),
 * tracks smart stops (indicator coverage sufficiency), and detects
 * diminishing returns (new-entity ratio per task).
 *
 * Emits `investigation:resource-update` events via the event bus.
 * Pure deterministic code — no LLM calls.
 */

import { eventBus } from '@/core/events';
import type {
  InvestigationTierConfig,
  ResourceState,
  CoverageReport,
} from './types';

// ---------------------------------------------------------------------------
// Resource check results
// ---------------------------------------------------------------------------

export type HardStopReason = 'token_budget' | 'node_ceiling' | 'edge_ceiling' | 'time_limit';
export type StopRecommendation = 'continue' | 'hard_stop' | 'sufficiency_reached' | 'diminishing_returns';

export interface ResourceCheck {
  canContinue: boolean;
  recommendation: StopRecommendation;
  reason: string;
  hardStopReason?: HardStopReason;
}

// ---------------------------------------------------------------------------
// Diminishing returns config
// ---------------------------------------------------------------------------

/** Number of consecutive low-gain tasks to trigger diminishing returns flag. */
const DIMINISHING_RETURNS_WINDOW = 3;

/** Ratio threshold below which a task is considered low-gain. */
const DIMINISHING_RETURNS_THRESHOLD = 0.1;

// ---------------------------------------------------------------------------
// Resource manager
// ---------------------------------------------------------------------------

export interface ResourceManager {
  /** Get current resource state snapshot. */
  getState(): ResourceState;

  /** Check if the investigation can continue or must stop. */
  check(): ResourceCheck;

  /** Record tokens consumed by a pipeline call. */
  recordTokens(tokens: number): void;

  /** Update node and edge counts. */
  updateGraphCounts(nodes: number, edges: number): void;

  /** Record an info-gain ratio for a completed task (new entities / total extracted). */
  recordInfoGain(ratio: number): void;

  /** Update coverage percentage from the coverage report. */
  updateCoverage(report: CoverageReport): void;

  /** Get the elapsed time in seconds since the investigation started. */
  getElapsedSeconds(): number;
}

export function createResourceManager(
  investigationId: string,
  tierConfig: InvestigationTierConfig,
  startTime: Date = new Date(),
): ResourceManager {
  let tokensUsed = 0;
  let nodeCount = 0;
  let edgeCount = 0;
  let coveragePercent = 0;
  const infoGainTrend: number[] = [];

  function getElapsedSeconds(): number {
    return Math.floor((Date.now() - startTime.getTime()) / 1000);
  }

  function getState(): ResourceState {
    const elapsed = getElapsedSeconds();
    const diminishingReturns = checkDiminishingReturns();

    return {
      tokensUsed,
      tokenBudget: tierConfig.tokenBudget,
      tokensPercent: tierConfig.tokenBudget > 0
        ? Math.round((tokensUsed / tierConfig.tokenBudget) * 100)
        : 0,
      nodeCount,
      nodeCeiling: tierConfig.nodeCeiling,
      edgeCount,
      edgeCeiling: tierConfig.edgeCeiling,
      elapsedSeconds: elapsed,
      timeLimitSeconds: tierConfig.timeLimitSeconds,
      coveragePercent,
      infoGainTrend: infoGainTrend.slice(-10), // last 10 tasks
      diminishingReturns,
    };
  }

  function checkDiminishingReturns(): boolean {
    if (infoGainTrend.length < DIMINISHING_RETURNS_WINDOW) return false;
    const recent = infoGainTrend.slice(-DIMINISHING_RETURNS_WINDOW);
    return recent.every(ratio => ratio < DIMINISHING_RETURNS_THRESHOLD);
  }

  function emitUpdate(): void {
    eventBus.emit('investigation:resource-update', {
      investigationId,
      resource: getState(),
    });
  }

  function check(): ResourceCheck {
    const elapsed = getElapsedSeconds();

    // Hard stops — non-negotiable limits
    if (tokensUsed >= tierConfig.tokenBudget) {
      return {
        canContinue: false,
        recommendation: 'hard_stop',
        reason: `Token budget exhausted: ${tokensUsed}/${tierConfig.tokenBudget}`,
        hardStopReason: 'token_budget',
      };
    }

    if (nodeCount >= tierConfig.nodeCeiling) {
      return {
        canContinue: false,
        recommendation: 'hard_stop',
        reason: `Node ceiling reached: ${nodeCount}/${tierConfig.nodeCeiling}`,
        hardStopReason: 'node_ceiling',
      };
    }

    if (edgeCount >= tierConfig.edgeCeiling) {
      return {
        canContinue: false,
        recommendation: 'hard_stop',
        reason: `Edge ceiling reached: ${edgeCount}/${tierConfig.edgeCeiling}`,
        hardStopReason: 'edge_ceiling',
      };
    }

    if (elapsed >= tierConfig.timeLimitSeconds) {
      return {
        canContinue: false,
        recommendation: 'hard_stop',
        reason: `Time limit reached: ${elapsed}s/${tierConfig.timeLimitSeconds}s`,
        hardStopReason: 'time_limit',
      };
    }

    // Smart stop — coverage sufficiency
    if (coveragePercent >= tierConfig.sufficiencyThreshold * 100) {
      return {
        canContinue: false,
        recommendation: 'sufficiency_reached',
        reason: `Coverage sufficiency reached: ${coveragePercent}% (threshold: ${tierConfig.sufficiencyThreshold * 100}%)`,
      };
    }

    // Diminishing returns — advisory signal
    if (checkDiminishingReturns()) {
      return {
        canContinue: true,
        recommendation: 'diminishing_returns',
        reason: `${DIMINISHING_RETURNS_WINDOW} consecutive tasks with <${DIMINISHING_RETURNS_THRESHOLD * 100}% new entities. Sources may be saturated.`,
      };
    }

    return {
      canContinue: true,
      recommendation: 'continue',
      reason: 'Within resource limits',
    };
  }

  function recordTokens(tokens: number): void {
    tokensUsed += tokens;
    emitUpdate();
  }

  function updateGraphCounts(nodes: number, edges: number): void {
    nodeCount = nodes;
    edgeCount = edges;
    emitUpdate();
  }

  function recordInfoGain(ratio: number): void {
    infoGainTrend.push(Math.max(0, Math.min(1, ratio)));
    emitUpdate();
  }

  function updateCoverage(report: CoverageReport): void {
    coveragePercent = report.overallPercentage;
    emitUpdate();
  }

  return {
    getState,
    check,
    recordTokens,
    updateGraphCounts,
    recordInfoGain,
    updateCoverage,
    getElapsedSeconds,
  };
}
